import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import analyticsService, { DropOffAnalysisData } from '../../services/analyticsService';

const DropOffAnalysis: React.FC = () => {
  const [data, setData] = useState<DropOffAnalysisData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDropOffData();
  }, []);

  const fetchDropOffData = async () => {
    try {
      setLoading(true);
      const dropOffData = await analyticsService.getDropOffAnalysis();
      setData(dropOffData);
    } catch (error) {
      console.error('Failed to fetch drop-off data:', error);
      toast.error('Failed to load drop-off analysis');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No drop-off data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Drop-off Analysis</h3>
        <p className="text-gray-600 mt-1">Identify where visitors abandon their conversations</p>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h4 className="text-md font-semibold text-gray-900 mb-4">Conversation Stage Drop-offs</h4>
        <div className="space-y-4">
          {data.stages.map((stage, index) => (
            <div key={index} className="border-b border-gray-200 pb-4 last:border-b-0">
              <div className="flex items-center justify-between mb-2">
                <h5 className="font-medium text-gray-900 capitalize">
                  {stage.stage.replace('_', ' ')}
                </h5>
                <span className="text-red-600 font-semibold">
                  {stage.drop_off_rate}% drop-off
                </span>
              </div>
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>{stage.total} total conversations</span>
                <span>{stage.dropped} dropped • {stage.continued} continued</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-blue-50 rounded-lg p-6">
        <h4 className="text-lg font-semibold text-blue-900 mb-2">Summary</h4>
        <p className="text-blue-800">
          Overall completion rate: <span className="font-bold">{data.summary.overall_completion_rate}%</span>
        </p>
        <p className="text-blue-700 text-sm mt-1">
          {data.summary.completed_leads} of {data.summary.total_conversations} conversations became leads
        </p>
      </div>
    </div>
  );
};

export default DropOffAnalysis;